import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Badge, Button, ErrorBanner, Modal, Select, TextInput } from '../../components/forms'
import { numberOrNull, describeError } from '../../lib/forms'
import { ApiError, api, newIdempotencyKey } from '../../lib/api'
import { formatMoneyDocument } from '../../lib/format'
import { useDebounced } from '../../lib/useDebounced'
import { useProduct } from '../../features/products/api'
import {
  lineKey,
  needsReference,
  tenderLabels,
  useCaptureCustomer,
  useCreditPosition,
  useCustomerSearch,
  useQuote,
  useSell,
  type ApprovalNeed,
  type CartLine,
  type PaymentMode,
  type PickedProduct,
  type PosContext,
  type PosCustomer,
  type SaleQuote,
  type SaleRequest,
  type Tender,
} from '../../features/pos/api'
import { ApprovalDialog, type ApprovalAnswer } from './ApprovalDialog'
import { ReceiptDialog } from './ReceiptDialog'
import { RegisterPanel } from './RegisterPanel'
import { MpesaTender } from './MpesaTender'
import { ExchangeCreditTender } from './ExchangeCreditTender'
import { Picture, ProductBrowser, RecentItems } from './ProductBrowser'
import { ShortageDialog } from './ShortageDialog'
import { ParkButton, ParkedSalesButton } from './ParkedSales'
import { AccountOrderButton, AccountOrdersButton } from './AccountOrder'
import { Permissions } from '../../auth/permissions'
import { useSession } from '../../auth/session'
import { usePageHeader } from '../../layout/pageHeader'
import { HeaderSlot } from '../../layout/HeaderSlot'
import { AccountIcon, BankIcon, BarcodeIcon, CardIcon, CartIcon, CashIcon, CreditNoteIcon, LockIcon, MinusIcon, PhoneIcon, PlusIcon, PrinterIcon, SearchIcon, TagIcon, TrashIcon, UserIcon } from './icons'

type LookupMatch = { id: string; code: string; name: string; barcode?: string; retailPrice: number; isActive: boolean }

/**
 * The ways a sale is paid, one tap each; the rest are under "Other".
 *
 * On account and a credit note were in that dropdown and are now tiles of their own: a trade customer
 * taking goods on their account is an everyday sale at this counter, not an exception, and a customer
 * spending a credit note is holding a piece of paper that says so. Two taps hidden behind a select
 * is how a cashier ends up ringing something as cash and fixing it afterwards.
 */
/**
 * The banks a sale can be paid into, in one place (D-232).
 *
 * The tile and the dropdown beside it both read this. They were two literals that had to agree, and
 * a bank added to one and not the other is a tender that can be chosen and then cannot be changed,
 * or one that is offered and never selectable.
 */
const BANKS: PaymentMode[] = ['EquityBank', 'SidianBank', 'DtbBank', 'AbsaBank']

const TILES: { label: string; icon: React.ReactNode; modes: PaymentMode[] }[] = [
  { label: 'Cash', icon: <CashIcon />, modes: ['Cash'] },
  { label: 'M-Pesa', icon: <PhoneIcon className="h-5 w-5 text-[#3AA935] group-aria-checked:text-white" />, modes: ['Mpesa'] },
  { label: 'Card', icon: <CardIcon />, modes: ['Card'] },
  { label: 'Bank', icon: <BankIcon />, modes: BANKS },
  { label: 'On account', icon: <AccountIcon />, modes: ['CreditSale'] },
  { label: 'Credit note', icon: <CreditNoteIcon />, modes: ['ExchangeCredit'] },
]
const OTHER: PaymentMode[] = ['Cheque', 'LoyaltyPoints']
const TENDERS: PaymentMode[] = ['Cash', 'Mpesa', 'Card', 'EquityBank', 'SidianBank', 'DtbBank', 'AbsaBank', 'Cheque', 'ExchangeCredit', 'LoyaltyPoints', 'CreditSale']
const RECENT = 8

function recentKey(locationId: string) {
  return `dpl.pos.recent.${locationId}`
}

function rememberedRecent(locationId: string): PickedProduct[] {
  try {
    const stored = JSON.parse(window.localStorage.getItem(recentKey(locationId)) ?? '[]')
    return Array.isArray(stored) ? (stored as PickedProduct[]).slice(0, RECENT) : []
  } catch {
    return []
  }
}

/**
 * A piece count, filled the way the shop would actually fill it (D-272).
 *
 * <b>Somebody asking for 150 of a hundred-box is asking for a box and fifty.</b> That is how the
 * goods leave the shelf, how the storeman counts what is left behind them, and — the point of it —
 * what the customer expects to pay: the box at the box price, and the fifty at what they cost
 * inside a box. Never 150 singles at shelf price. Keying 150 used to leave the cashier to work the
 * split out and press the two toggles themselves, and the one who forgot charged a wholesale
 * customer retail for the lot.
 *
 * <b>The arithmetic is the only thing here.</b> Both lines are still priced by the server, from the
 * one box price the owner set: the box line at that price, the loose line at a hundredth of it. The
 * screen has not learnt to price anything.
 *
 * A box line is left alone — its quantity already counts boxes, so there is nothing to work out.
 */
function inPacks(line: CartLine, pieces: number): CartLine[] {
  const size = line.packSize ?? 1

  // packDescription is set only where the shop sells this by the box AND has priced the box
  // (D-204). With no box price there is no box rate, and the server refuses the line outright.
  if (line.asPack === true || size <= 1 || !line.packDescription || pieces < size) {
    return [{ ...line, quantity: pieces }]
  }

  const boxes = Math.floor(pieces / size)
  // Rounded because cable is sold by the metre: 40.5 less two boxes of 20 is 0.4999999999999982 in
  // binary floating point, which would put a line of nothing on the sale.
  const loose = Math.round((pieces - (boxes * size)) * 100) / 100

  const box: CartLine = { ...line, asPack: true, atPackRate: false, quantity: boxes }

  return loose > 0 ? [box, { ...line, asPack: false, atPackRate: true, quantity: loose }] : [box]
}

/**
 * Lines in the same unit, added together.
 *
 * The fill above can produce a box line for a product the basket already holds a box of, and two
 * lines keyed the same are two lines nobody can tell apart — the mistake lineKey exists to prevent.
 * The line already in the basket keeps its own discount, as switching a line's unit does.
 */
function merged(lines: CartLine[]): CartLine[] {
  const out: CartLine[] = []

  for (const line of lines) {
    const key = lineKey(line.productId, line.asPack, line.atPackRate)
    const at = out.findIndex((o) => lineKey(o.productId, o.asPack, o.atPackRate) === key)

    if (at < 0) {
      out.push(line)
    } else {
      out[at] = { ...out[at], quantity: out[at].quantity + line.quantity }
    }
  }

  return out
}

/** One line of the basket re-keyed to that many pieces — as boxes and loose, where that is what it is. */
function filled(cart: CartLine[], key: string, pieces: number): CartLine[] {
  const line = cart.find((l) => lineKey(l.productId, l.asPack, l.atPackRate) === key)
  if (!line) return cart

  const parts = inPacks(line, pieces)

  // Nothing to do, and the SAME array handed back rather than a copy of it: every basket change
  // re-quotes, and this runs whenever the quantity box is left, including when it was only looked at.
  if (parts.length === 1 && parts[0].quantity === line.quantity) {
    return cart
  }

  return merged(cart.flatMap((l) => (lineKey(l.productId, l.asPack, l.atPackRate) === key ? parts : [l])))
}

/**
 * The till.
 *
 * Every figure in the sale — unit price, discount, VAT, total — is the server's quote.
 * The screen never prices anything itself: a second pricing engine in the browser
 * disagrees with the first the moment a contract price or a VAT exemption applies.
 * The shelf beside it shows list prices, to find things by.
 */
export function Till({ context, onChangeLocation }: { context: PosContext; onChangeLocation?: () => void }) {
  const locationId = context.location.id
  const session = context.session!

  const [cart, setCart] = useState<CartLine[]>([])
  const [customer, setCustomer] = useState<PosCustomer>(context.walkIn)
  const [tenders, setTenders] = useState<Tender[]>([{ mode: 'Cash', amount: 0 }])
  const [lastAdded, setLastAdded] = useState<string | null>(null)
  const [approval, setApproval] = useState<{ needs: ApprovalNeed[]; sellerCanApprove: boolean } | null>(null)
  // The receipt on screen; `fresh` when it is the sale just made, which closing it clears.
  const [receipt, setReceipt] = useState<{ id: string; points: number; fresh: boolean } | null>(null)
  const [lastReceipt, setLastReceipt] = useState<{ id: string; points: number } | null>(null)
  const [chooser, setChooser] = useState<LookupMatch[] | null>(null)
  const can = useSession((st) => st.can)
  const [lpo, setLpo] = useState('')

  // VAT off for this sale, and why (D-222). Held together because neither is any use alone: the
  // server refuses the switch without a reason, and the reason is printed on the invoice.
  const [vatOff, setVatOff] = useState(false)
  const [vatOffReason, setVatOffReason] = useState('')
  const [search, setSearch] = useState('')
  const [recent, setRecent] = useState<PickedProduct[]>(() => rememberedRecent(locationId))
  // A line the shop cannot fill, held open until the cashier decides what to do about it (D-193).
  // A dialog rather than the toast above, because "bring it in from Westlands" raises a transfer and
  // sends a customer away expecting a telephone call — not a decision for something that vanishes
  // after five seconds.
  const [shortage, setShortage] = useState<{ productId: string; name: string; requested: number; available: number; asPack?: boolean } | null>(null)

  // One key per basket. A retry after a dropped connection — or after an approval —
  // resends the same key and gets the first sale back rather than charging twice;
  // a changed basket is a new sale and gets a new key.
  const basketSignature = JSON.stringify({ cart, customer: customer.id, tenders })
  const keyRef = useRef<{ signature: string; key: string } | null>(null)

  function idempotencyKeyFor(signature: string): string {
    if (keyRef.current?.signature !== signature) {
      keyRef.current = { signature, key: newIdempotencyKey() }
    }
    return keyRef.current.key
  }

  const onAccount = tenders.some((t) => t.mode === 'CreditSale')

  // Asked for only once an account customer is on the sale, and read from the one place that counts
  // open orders as well as invoices (D-199). Falls back to the figures the till already has, so a
  // slow answer never leaves the panel blank.
  const credit = useCreditPosition(customer.isWalkIn ? null : customer.id)
  const available = credit.data?.available ?? customer.availableCredit

  const baseRequest: SaleRequest = {
    customerId: customer.id,
    locationId,
    registerSessionId: session.id,
    lines: cart.map((l) => ({ productId: l.productId, quantity: l.quantity, discountPercent: l.discountPercent, discountAmount: l.discountAmount, asPack: l.asPack === true, atPackRate: l.atPackRate === true })),
    payments: onAccount
      ? [{ mode: 'CreditSale', amount: 1 }]
      : tenders.filter((t) => t.amount > 0).map((t) => ({ mode: t.mode, amount: t.amount, reference: t.reference, mpesaPaymentId: t.mpesaPaymentId, creditNoteId: t.creditNoteId })),
    customerOrderNumber: onAccount && lpo.trim() ? lpo.trim() : undefined,
    vatOff: vatOff || undefined,
    vatOffReason: vatOff && vatOffReason.trim() ? vatOffReason.trim() : undefined,
  }

  // Only what changes the price goes into the quote; typing a tender amount does not.
  const quoteRequest = useMemo<SaleRequest>(
    () => ({ ...baseRequest, payments: onAccount ? [{ mode: 'CreditSale', amount: 1 }] : [] }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the priced parts only
    [JSON.stringify(cart), customer.id, onAccount, vatOff],
  )

  const queries = useQueryClient()
  const quote = useQuote(cart.length > 0 ? quoteRequest : null)

  // The quote holds the stock (D-193), so the moment it comes back the tiles behind it are wrong by
  // exactly what this basket is holding. Refreshing them here means a cashier sees the shelf go down
  // as they scan, rather than waiting on the twenty-second poll to tell them what they just did.
  useEffect(() => {
    if (quote.data) {
      void queries.invalidateQueries({ queryKey: ['products', 'list'] })
    }
  }, [quote.data, queries])

  // The server found a line it could not hold — another counter took it between this till reading the
  // shelf and the basket being priced. The client guard above catches the ordinary case; this catches
  // the race, which is the one the holds exist for.
  useEffect(() => {
    const found = quote.data?.shortages?.[0]

    if (found) {
      setShortage({
        productId: found.productId,
        name: found.name,
        requested: found.requested,
        available: found.available,
      })
    }
  }, [quote.data?.shortages])
  const sell = useSell(locationId)
  const q = cart.length > 0 ? quote.data : undefined

  const total = q?.grandTotal ?? 0

  // Over what they have left, counting their open orders. Shown while the customer is still at the
  // counter, so part of it can be taken another way instead of the sale being refused at the end.
  const overLimit = onAccount && total > available
  const tendered = tenders.reduce((sum, t) => sum + (t.mode === 'CreditSale' ? 0 : t.amount), 0)
  const change = tendered - total
  const short = !onAccount && cart.length > 0 && change < -0.004
  // An M-Pesa tender is money that arrived, or nothing (D-082).
  const unpaidMpesa = !onAccount && tenders.some((t) => t.mode === 'Mpesa' && t.amount > 0 && !t.mpesaPaymentId)
  const needsLpo = onAccount && customer.requiresPurchaseOrder === true && !lpo.trim()
  // An exchange credit is spent against its credit note, and gives no change: change is cash.
  const unclaimedCredit = !onAccount && tenders.some((t) => t.mode === 'ExchangeCredit' && t.amount > 0 && !t.creditNoteId)
  const creditOver = tenders.some((t) => t.mode === 'ExchangeCredit' && t.creditRemaining !== undefined && t.amount > t.creditRemaining + 0.004)
  const creditChange = !onAccount && change > 0.004 && tenders.some((t) => t.mode === 'ExchangeCredit' && t.amount > 0)
  // Points are whole shillings, the member's own, and give no change either (M18).
  const pointsTendered = onAccount ? 0 : tenders.filter((t) => t.mode === 'LoyaltyPoints').reduce((sum, t) => sum + t.amount, 0)
  const pointsProblem = pointsTendered <= 0 ? null
    : pointsTendered !== Math.trunc(pointsTendered) ? 'Points are whole shillings.'
    : pointsTendered > (q?.loyaltyPoints ?? 0) ? `${customer.name} has ${(q?.loyaltyPoints ?? 0).toLocaleString('en-KE')} points.`
    : change > 0.004 ? 'Points give no change: spend fewer.'
    : null
  const blocked = unpaidMpesa || unclaimedCredit || creditOver || creditChange || pointsProblem !== null
  const canCharge = cart.length > 0 && !short && !blocked && !needsLpo && !q?.refusal && !quote.isFetching
    && !(vatOff && !vatOffReason.trim())

  /**
   * The most of a product this sale may take: what the shop can sell now, from the
   * latest quote, else from the shelf. Unknown until one of them has said; the quote's
   * refusal and the server stand behind it either way.
   */
  function limitOf(productId: string, fromShelf?: number): number | undefined {
    if (context.allowNegativeStock) return undefined
    // Any line for this product will do: available is the shelf's figure in pieces, the same for
    // every unit of the same product.
    return q?.lines.find((l) => l.productId === productId)?.available ?? fromShelf
  }

  /** What the basket already takes off the shelf for one product, in pieces across every unit. */
  function piecesOf(productId: string): number {
    return cart
      .filter((l) => l.productId === productId)
      .reduce((sum, l) => sum + l.quantity * (l.asPack && l.packSize ? l.packSize : 1), 0)
  }

  function add(product: PickedProduct) {
    // The unit the shelf was switched to (D-210). A box and a loose one of the same thing are two
    // lines at two prices, so this is part of which line is being added to.
    const asPack = product.asPack === true && product.packSize !== undefined
    const key = lineKey(product.id, asPack)
    const pieces = asPack ? (product.packSize ?? 1) : 1

    const limit = limitOf(product.id, product.available)

    // Compared in PIECES across every unit of this product: a basket holding one box of six and
    // one loose has taken seven off the shelf, and a limit read against boxes alone would let a
    // cashier promise stock that is already spoken for.
    if (limit !== undefined && piecesOf(product.id) + pieces > limit) {
      // The dialog rather than the toast: a cashier whose customer wants four of something the shop
      // has one of needs somewhere to go next, and "out of stock here" on its own is a dead end.
      setShortage({
        productId: product.id,
        name: product.name,
        requested: Math.floor(limit / pieces) + 1,
        available: Math.max(0, Math.floor(limit / pieces)),
        asPack,
      })
      return
    }
    setCart((current) => {
      const existing = current.find((l) => lineKey(l.productId, l.asPack, l.atPackRate) === key)
      if (existing) {
        // Tapped up to a whole box, the line becomes one (D-272). The shelf adds a piece at a time,
        // and the hundredth piece of a hundred-box is a box — priced as one, exactly as a keyed 100 is.
        return asPack
          ? current.map((l) => (lineKey(l.productId, l.asPack, l.atPackRate) === key ? { ...l, quantity: l.quantity + 1 } : l))
          : filled(current, key, existing.quantity + 1)
      }
      return [...current, {
        productId: product.id,
        code: product.code,
        name: product.name,
        imageUrl: product.imageUrl,
        quantity: 1,
        discountPercent: 0,
        discountAmount: 0,
        asPack,
        packSize: product.packSize,
        packDescription: product.packDescription,
      }]
    })
    setLastAdded(product.id)
    setRecent((current) => {
      // Not the stock figure: remembered, it would be out of date.
      const next = [{ ...product, available: undefined }, ...current.filter((p) => p.id !== product.id)].slice(0, RECENT)
      try {
        window.localStorage.setItem(recentKey(locationId), JSON.stringify(next))
      } catch {
        // Only a convenience.
      }
      return next
    })
  }

  /**
   * Changes one basket line, found by its product AND its unit (D-210).
   *
   * Keyed on the product alone, a change meant for the box line landed on the loose one as well —
   * and switching a line's unit would have collided with the other line rather than moving it.
   */
  /**
   * Adds loose pieces beside a box, at what they cost inside it (D-235).
   *
   * A separate line rather than more of the box, because they are not boxes: 1 box + 3 loose is
   * thirteen pieces off the shelf and two prices on the receipt, which is what the customer asked
   * for and what the ledger has to say.
   */
  function addLooseAtBoxRate(line: CartLine) {
    const key = lineKey(line.productId, false, true)

    setCart((current) => {
      const existing = current.find((l) => lineKey(l.productId, l.asPack, l.atPackRate) === key)

      if (existing) {
        return current.map((l) => (lineKey(l.productId, l.asPack, l.atPackRate) === key ? { ...l, quantity: l.quantity + 1 } : l))
      }

      return [...current, {
        productId: line.productId,
        code: line.code,
        name: line.name,
        imageUrl: line.imageUrl,
        quantity: 1,
        discountPercent: 0,
        discountAmount: 0,
        asPack: false,
        atPackRate: true,
        packSize: line.packSize,
        packDescription: line.packDescription,
      }]
    })
  }

  /**
   * Whether this line may hold that many of its own unit, with the shortage dialog where it may not.
   *
   * Shared by the typed quantity and by the fill below, so a quantity refused one way is refused
   * the other.
   */
  function withinShelf(line: CartLine, quantity: number): boolean {
    // In pieces, counting the other units of this product that are already in the basket.
    const perUnit = line.asPack && line.packSize ? line.packSize : 1
    const limit = limitOf(line.productId)

    if (limit === undefined) return true

    const others = piecesOf(line.productId) - (line.quantity * perUnit)

    if (others + (quantity * perUnit) <= limit) return true

    setShortage({
      productId: line.productId,
      name: line.name,
      requested: quantity,
      available: Math.max(0, Math.floor((limit - others) / perUnit)),
      asPack: line.asPack,
    })

    return false
  }

  /**
   * A quantity the cashier has FINISHED keying, filled as boxes and loose pieces (D-272).
   *
   * Separate from update() because of when it runs: when the field is left, or Enter is pressed, or
   * − and + are tapped — never on a keystroke. Keying "150" into a box of twelve passes through 15
   * on the way, and splitting there takes the field out from under the "0" still to come.
   */
  function fill(key: string, pieces: number) {
    const line = cart.find((l) => lineKey(l.productId, l.asPack, l.atPackRate) === key)
    if (!line) return
    if (!withinShelf(line, pieces)) return

    setCart((current) => filled(current, key, pieces))
  }

  function update(key: string, change: Partial<CartLine>) {
    const line = cart.find((l) => lineKey(l.productId, l.asPack, l.atPackRate) === key)
    if (!line) return

    // Switching a line's own unit is a move, not an edit: if the basket already holds that product
    // in the unit being switched to, the two lines become one rather than two lines nobody can
    // tell apart.
    if (change.asPack !== undefined && change.asPack !== (line.asPack === true)) {
      const target = lineKey(line.productId, change.asPack, false)
      const existing = cart.find((l) => lineKey(l.productId, l.asPack, l.atPackRate) === target)

      setCart((current) =>
        existing
          ? current
              .filter((l) => lineKey(l.productId, l.asPack, l.atPackRate) !== key)
              .map((l) => (lineKey(l.productId, l.asPack, l.atPackRate) === target ? { ...l, quantity: l.quantity + line.quantity } : l))
          : current.map((l) => (lineKey(l.productId, l.asPack, l.atPackRate) === key ? { ...l, ...change } : l)))

      return
    }

    if (change.quantity !== undefined && !withinShelf(line, change.quantity)) {
      // The line is left where it was rather than silently clipped to the limit. Clipping made the
      // number change under the cashier's finger while the dialog explained why — two answers to one
      // action. They now choose: take what is here, bring the rest in, or drop the line.
      return
    }
    setCart((current) => current.map((l) => (lineKey(l.productId, l.asPack, l.atPackRate) === key ? { ...l, ...change } : l)))
  }

  function setTender(index: number, change: Partial<Tender>) {
    setTenders((current) => current.map((t, i) => (i === index ? { ...t, ...change } : t)))
  }

  function changeMode(index: number, mode: PaymentMode) {
    setTender(index, { mode, mpesaPaymentId: undefined, mpesaCode: undefined, creditNoteId: undefined, creditNoteCode: undefined, creditRemaining: undefined })
  }

  function exact(index: number) {
    const others = tenders.reduce((sum, t, i) => sum + (i === index ? 0 : t.amount), 0)
    setTender(index, { amount: Math.max(0, Math.round((total - others) * 100) / 100) })
  }

  function reset() {
    keyRef.current = null
    setCapturedPayer(null)
    setCart([])
    setCustomer(context.walkIn)
    setTenders([{ mode: 'Cash', amount: 0 }])
    setLpo('')
    setLastAdded(null)
    setApproval(null)
    sell.reset()
  }

  /**
   * What to do with the person whose M-Pesa number just paid (D-202).
   *
   * **A record this payment created is simply used.** It is a brand-new retail customer with the
   * walk-in's own prices, so attaching the sale to it changes nothing the cashier has read out — and
   * it means a receipt, a warranty claim and a telephone call all have somebody to point at.
   *
   * **An account that already existed is asked about, never assumed.** It may be on a wholesale tier
   * or carry credit terms, and switching the sale to it would re-price the basket after the total was
   * announced. So the cashier is shown who it is and decides.
   */
  function capturePayer(payer: { id: string; name: string; wasCreated: boolean }) {
    if (!customer.isWalkIn) {
      return
    }

    if (payer.wasCreated) {
      setCapturedPayer({ ...payer, applied: true })
      void attachCustomer(payer.id)
      return
    }

    setCapturedPayer({ ...payer, applied: false })
  }

  /** Puts the customer on the sale and re-prices, exactly as choosing them by hand would. */
  async function attachCustomer(id: string) {
    const found = await api<PosCustomer>(`/api/pos/customers/${id}`)
    setCustomer(found)
  }

  function charge(answer?: ApprovalAnswer) {
    const request: SaleRequest = {
      ...baseRequest,
      approval: answer && 'approverUserId' in answer ? { approverUserId: answer.approverUserId, pin: answer.pin } : undefined,
      approvalReason: answer?.reason,
    }

    sell.mutate(
      { request, idempotencyKey: idempotencyKeyFor(basketSignature) },
      {
        onSuccess: (result) => {
          setApproval(null)
          setReceipt({ id: result.invoiceId, points: result.pointsEarned ?? 0, fresh: true })
          setLastReceipt({ id: result.invoiceId, points: result.pointsEarned ?? 0 })
        },
        onError: (error) => {
          // 409 is a question — "who approves this?" — not a failure.
          if (error instanceof ApiError && error.status === 409 && Array.isArray(error.extensions.approvals)) {
            setApproval({
              needs: error.extensions.approvals as ApprovalNeed[],
              sellerCanApprove: error.extensions.sellerCanApprove === true,
            })
          }
        },
      },
    )
  }

  // F2 goes back to the scanner from anywhere; F9 charges. A till is driven from the
  // keyboard, and a mouse trip per sale adds up over a day.
  const scanRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'F2') {
        event.preventDefault()
        scanRef.current?.focus()
      }
      if (event.key === 'F9' && canCharge && !sell.isPending) {
        event.preventDefault()
        charge()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  // On a phone the sale sits below the shelf: a bar at the foot of the screen says
  // what is in it and goes there, until the sale itself is in view.
  const saleRef = useRef<HTMLElement>(null)
  const [saleInView, setSaleInView] = useState(false)

  // Who paid, when the till has just learnt it from an M-Pesa payment (D-202).
  const [capturedPayer, setCapturedPayer] = useState<{ id: string; name: string; wasCreated: boolean; applied: boolean } | null>(null)
  useEffect(() => {
    const el = saleRef.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(([entry]) => setSaleInView(entry.isIntersecting), { threshold: 0.05 })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const lineNeeds = (productId: string) => (q?.approvals ?? []).filter((n) => n.productId === productId)
  const rates = [...new Set((q?.lines ?? []).map((l) => l.vatRate))]
  const vatLabel = q?.vatExemptionCertificate
    ? 'VAT (exempt)'
    : vatOff
      ? 'VAT (off)'
      : context.pricesIncludeVat ? 'VAT included' : rates.length === 1 ? `VAT (${rates[0]}%)` : 'VAT'

  // A customer holding a certificate is already exempt on every line; offering to switch off what
  // is already off would just be a second way to say the same thing, and a confusing one.
  const mayWaiveVat = can(Permissions.Sales.WaiveVat) && !q?.vatExemptionCertificate

  return (
    <div className="space-y-5 pb-20 xl:pb-0">
      <TillHeader context={context} onChangeLocation={onChangeLocation} />

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_33rem] xl:grid-rows-[auto_1fr] 2xl:grid-cols-[minmax(0,1fr)_36rem]">
        <div className="min-w-0 space-y-4 xl:col-start-1 xl:row-start-1">
          <Scanner inputRef={scanRef} locationId={locationId} term={search} onTerm={setSearch} onAdd={add} onChoose={setChooser} />
          <ProductBrowser locationId={locationId} search={search.trim().length >= 2 ? search.trim() : ''} pricesIncludeVat={context.pricesIncludeVat} onAdd={add} />
        </div>

        <aside ref={saleRef} aria-label="Current sale" className="min-w-0 xl:col-start-2 xl:row-span-2 xl:row-start-1">
          <section className="rounded-lg border border-dpl-border bg-dpl-surface shadow-sm">
            <header className="flex items-center justify-between gap-2 border-b border-dpl-border px-4 py-3.5 sm:px-5">
              <h2 className="flex items-center gap-2 text-lg font-semibold">
                <CartIcon className="h-6 w-6 text-dpl-primary" />
                Current sale ({cart.length})
              </h2>
              {cart.length > 0 && <Button tone="ghost" className="px-2 text-dpl-primary" onClick={reset}>Clear all</Button>}
            </header>

            <div className="space-y-4 p-4 sm:p-5">
              <CustomerPanel locationId={locationId} customer={customer} walkIn={context.walkIn} onChange={setCustomer} />

              {q?.loyaltyTier && (
                <p className="rounded-md bg-dpl-accent/10 px-3 py-2 text-sm" aria-label="Loyalty">
                  <span className="font-medium">{q.loyaltyTier} member</span>
                  {q.loyaltyDiscountPercent > 0 && ` · ${q.loyaltyDiscountPercent}% loyalty price`}
                  {` · ${(q.loyaltyPoints ?? 0).toLocaleString('en-KE')} points`}
                </p>
              )}

              {cart.length === 0 ? (
                <p className="rounded-md border border-dashed border-dpl-border py-8 text-center text-sm text-dpl-muted">
                  Scan a barcode or tap a product to start a sale.
                  <span className="block text-xs">F2 returns to the scanner, F9 charges.</span>
                </p>
              ) : (
                <Basket
                  cart={cart}
                  quote={q}
                  limited={!context.allowNegativeStock}
                  lineNeeds={lineNeeds}
                  onUpdate={update}
                  onFill={fill}
                  onRemove={(key) => setCart(cart.filter((l) => lineKey(l.productId, l.asPack, l.atPackRate) !== key))}
                  onAddLooseAtBoxRate={addLooseAtBoxRate}
                />
              )}

              {lastAdded && <Suggestions productId={lastAdded} inCart={cart.map((l) => l.productId)} onAdd={add} />}

              {cart.length > 0 && <SaleDiscount cart={cart} limit={context.maxDiscountPercent} managerCeiling={context.managerDiscountCeilingPercent} onApply={(percent) => setCart(cart.map((l) => ({ ...l, discountPercent: percent, discountAmount: 0 })))} />}

              {/* Park what is on the counter, or pick up something parked earlier (D-197). Both sit
                  with the sale rather than in a menu: a customer saying "I have forgotten my card"
                  is a thing that happens mid-sale, with somebody waiting behind them. */}
              <div className="flex flex-wrap gap-2">
                <ParkButton
                  locationId={locationId}
                  registerSessionId={session.id}
                  customerId={customer.id}
                  cart={cart}
                  onParked={reset}
                  holdHours={context.parkedSaleHoldHours}
                  holdChoices={context.parkedSaleHoldChoices}
                />

                {/* D-199: an account customer may take the goods away on their account instead of
                    paying now — an order and a proforma rather than a sale and a receipt. It sits
                    beside parking because both are things a cashier does INSTEAD of charging. */}
                <AccountOrderButton
                  locationId={locationId}
                  customer={customer}
                  cart={cart}
                  onTaken={reset}
                />

                {/* D-200: and what this customer already has open — confirm it, invoice it when the
                    goods go out, or call it off. The button hides itself when they have none. */}
                <AccountOrdersButton
                  locationId={locationId}
                  registerSessionId={session.id}
                  customer={customer}
                  onInvoiced={(sale) => {
                    // The same receipt dialogue a cash sale opens: it is a real invoice now.
                    setReceipt({ id: sale.invoiceId, points: sale.pointsEarned, fresh: true })
                  }}
                />

                <ParkedSalesButton
                  locationId={locationId}
                  registerSessionId={session.id}
                  /* Picking one up replaces what is on the counter — and the till holds its stock
                     under one key, so the basket in progress would lose its hold as well. The list
                     says to finish or park that one first rather than taking it away silently. */
                  busyWithASale={cart.length > 0}
                  onResume={(sale) => {
                    // The basket comes back as the cashier left it. It is re-priced by the quote
                    // that follows, because a price kept for three days is one a customer can sit
                    // on and then expect.
                    // And re-filled as boxes and loose on the way back in (D-272): a basket parked
                    // as 150 pieces comes back as the box and the fifty it was always going to be,
                    // rather than 150 singles at shelf price.
                    setCart(merged(sale.lines.flatMap((l) => inPacks({
                      productId: l.productId,
                      code: l.code,
                      name: l.name,
                      quantity: l.quantity,
                      discountPercent: l.discountPercent,
                      discountAmount: l.discountAmount,
                      // In the unit it was parked in (D-210). Restored as singles, two boxes of six
                      // came back as two bulbs — the customer's own sale, re-rung at a twelfth of
                      // what they had agreed.
                      asPack: l.asPack,
                      packSize: l.piecesPerUnit > 1 ? l.piecesPerUnit : undefined,
                      packDescription: l.packDescription,
                    }, l.quantity))))

                    // And the customer comes back with it. An account sale parked at four o'clock
                    // must not be rung through as a walk-in at five, at retail prices and with no
                    // LPO asked for.
                    setCustomer(sale.customer)
                    setTenders([{ mode: 'Cash', amount: 0 }])
                  }}
                />
              </div>

              <dl className="overflow-hidden rounded-lg bg-dpl-bg text-sm">
                <div className="space-y-2 px-4 py-3">
                  <Row label="Subtotal" value={q?.subTotal} />
                  {(q?.discountTotal ?? 0) > 0 && <Row label="Discount" value={-(q?.discountTotal ?? 0)} />}
                  <Row label={vatLabel} value={q?.vatTotal} />

                  {mayWaiveVat && (
                    <div className="border-t border-dpl-border pt-2">
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={vatOff}
                          onChange={(e) => {
                            setVatOff(e.target.checked)

                            // Clearing it on the way off matters: a reason left behind would be
                            // attached to the next sale somebody switches off for a different one.
                            if (!e.target.checked) setVatOffReason('')
                          }}
                          className="h-4 w-4 rounded border-dpl-border"
                        />
                        <span>Sell without VAT</span>
                      </label>

                      {vatOff && (
                        <div className="mt-2 space-y-1">
                          <input
                            type="text"
                            value={vatOffReason}
                            onChange={(e) => setVatOffReason(e.target.value)}
                            placeholder="Why? (printed on the invoice)"
                            aria-label="Reason VAT is off"
                            className="w-full rounded-md border border-dpl-border px-2 py-1.5 text-sm"
                          />
                          {/* Said here rather than discovered when the sale is refused. */}
                          <p className="text-xs text-dpl-muted">
                            The VAT inside the list price comes off, so the customer pays less. It is not kept.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-baseline justify-between bg-dpl-primary/[0.08] px-4 py-3">
                  <dt className="text-xl font-bold">Total</dt>
                  <dd className="text-3xl font-bold tabular-nums text-dpl-primary-hover" aria-live="polite">{formatMoneyDocument(total)}</dd>
                </div>
              </dl>

              {q?.refusal && (
                <p role="alert" className="rounded-md bg-dpl-danger/5 px-3 py-2 text-sm text-dpl-danger">{q.refusal}</p>
              )}

              <div>
                <p id="payment-method" className="mb-2 text-sm font-bold">Payment method</p>
                <div role="radiogroup" aria-labelledby="payment-method" className="grid grid-cols-3 gap-2">
                  {TILES.map((tile) => {
                    const selected = tile.modes.includes(tenders[0].mode)

                    // On account needs somebody to put it on: the walk-in record is shared by every
                    // anonymous sale in the company, so there would be nobody to invoice.
                    const barred = tile.modes[0] === 'CreditSale' && customer.isWalkIn

                    return (
                      <button
                        key={tile.label}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        title={barred ? 'Take the customer\u2019s details first \u2014 an account sale needs a name' : undefined}
                        disabled={Boolean(tenders[0].mpesaPaymentId) || barred}
                        onClick={() => !selected && changeMode(0, tile.modes[0])}
                        className={`group flex flex-col items-center gap-1 rounded-lg border px-1 py-3 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-60 sm:text-sm ${
                          selected ? 'border-dpl-primary bg-dpl-primary text-white shadow-sm' : 'border-dpl-border bg-dpl-surface text-dpl-text hover:border-dpl-primary/40'
                        }`}
                      >
                        {tile.icon}
                        {tile.label}
                      </button>
                    )
                  })}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Select
                    aria-label="Other payment method"
                    className="w-auto min-w-0 flex-1"
                    value={OTHER.includes(tenders[0].mode) ? tenders[0].mode : ''}
                    disabled={Boolean(tenders[0].mpesaPaymentId)}
                    onChange={(e) => e.target.value && changeMode(0, e.target.value as PaymentMode)}
                  >
                    <option value="">Other: cheque, loyalty points…</option>
                    {OTHER.map((m) => (
                      <option key={m} value={m} disabled={m === 'LoyaltyPoints' && !q?.loyaltyPoints}>{tenderLabels[m]}</option>
                    ))}
                  </Select>
                  {BANKS.includes(tenders[0].mode) && (
                    <Select aria-label="Which bank" className="w-auto flex-1" value={tenders[0].mode} onChange={(e) => changeMode(0, e.target.value as PaymentMode)}>
                      {BANKS.map((m) => <option key={m} value={m}>{tenderLabels[m]}</option>)}
                    </Select>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                {tenders.map((tender, index) => (
                  <TenderRow
                    key={index}
                    index={index}
                    tender={tender}
                    locationId={locationId}
                    customer={customer}
                    loyaltyPoints={q?.loyaltyPoints}
                    total={total}
                    others={tenders.reduce((sum, t, i) => sum + (i === index ? 0 : t.amount), 0)}
                    onMode={(mode) => changeMode(index, mode)}
                    onChange={(change) => setTender(index, change)}
                    onExact={() => exact(index)}
                    onRemove={tenders.length > 1 ? () => setTenders(tenders.filter((_, i) => i !== index)) : undefined}
                    onPayer={capturePayer}
                  />
                ))}
                {!onAccount && (
                  <Button tone="ghost" className="px-2 text-dpl-primary" onClick={() => setTenders([...tenders, { mode: 'Mpesa', amount: 0 }])}>
                    + Split payment
                  </Button>
                )}
              </div>

              {onAccount ? (
                <div className="space-y-2">
                  <p className="text-sm font-medium">On account to {customer.name}</p>

                  {/* THE SAME ARITHMETIC AS EVERYWHERE ELSE (D-199). This read limit less balance,
                      which ignores what they have on open orders — so the till offered credit that
                      Customer 360 and the order screen both said was already spoken for. */}
                  <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 rounded-lg bg-dpl-bg px-3 py-2 text-sm">
                    <dt className="text-dpl-muted">Credit limit</dt>
                    <dd className="text-right tabular-nums">{formatMoneyDocument(credit.data?.creditLimit ?? customer.creditLimit)}</dd>
                    <dt className="text-dpl-muted">Owing on invoices</dt>
                    <dd className="text-right tabular-nums">{formatMoneyDocument(credit.data?.outstanding ?? customer.balance)}</dd>
                    {(credit.data?.committed ?? 0) > 0 && (
                      <>
                        <dt className="text-dpl-muted">On open orders</dt>
                        <dd className="text-right tabular-nums">{formatMoneyDocument(credit.data!.committed)}</dd>
                      </>
                    )}
                    <dt className="font-semibold">Left to spend</dt>
                    <dd className={`text-right font-semibold tabular-nums ${overLimit ? 'text-dpl-danger' : ''}`}>
                      {formatMoneyDocument(available)}
                    </dd>
                  </dl>

                  {/* Said before the Charge button is pressed, not after it is refused: the cashier
                      can still take part of it in cash while the customer is standing there. */}
                  {overLimit && (
                    <p className="text-sm text-dpl-danger">
                      {formatMoneyDocument(total - available)} more than {customer.name} has left. A manager can approve it,
                      or take the difference another way.
                    </p>
                  )}

                  {customer.requiresPurchaseOrder && (
                    <TextInput aria-label="LPO number" placeholder="Their LPO number (required)" className="uppercase" value={lpo} onChange={(e) => setLpo(e.target.value)} />
                  )}
                </div>
              ) : (
                cart.length > 0 && (
                  <div>
                    <p className={`text-sm font-medium ${short || creditChange ? 'text-dpl-danger' : 'text-dpl-success'}`} aria-live="polite">
                      {short ? `Short by ${formatMoneyDocument(-change)}` : creditChange ? `Over by ${formatMoneyDocument(change)} — an exchange credit gives no change` : `Change ${formatMoneyDocument(Math.max(0, change))}`}
                    </p>
                    {creditOver && <p className="text-xs text-dpl-danger">More than is left on the credit note.</p>}
                    {pointsProblem && <p className="text-xs text-dpl-danger">{pointsProblem}</p>}
                  </div>
                )
              )}

              {/* D-202: who just paid. A record the payment created is already on the sale and this
                  only says so — a cashier who is never told would find a customer they did not add.
                  An account that existed is a question, because ringing the sale to it may re-price
                  the basket. */}
              {capturedPayer && (
                <div className="rounded-md border border-dpl-border bg-dpl-bg px-3 py-2 text-sm">
                  {capturedPayer.applied ? (
                    <p>
                      <strong>{capturedPayer.name}</strong> is now on file from this payment, and this sale is theirs.
                      No marketing consent was taken.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      <p>That number is <strong>{capturedPayer.name}</strong>, already on file. Ring this sale to them?</p>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          tone="secondary"
                          onClick={async () => {
                            await attachCustomer(capturedPayer.id)
                            setCapturedPayer({ ...capturedPayer, applied: true })
                          }}
                        >
                          Yes, it is them
                        </Button>
                        <Button tone="ghost" onClick={() => setCapturedPayer(null)}>
                          No, somebody else paid
                        </Button>
                      </div>
                      <p className="text-xs text-dpl-muted">
                        Their prices or credit terms may differ, so the total can change.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {sell.error && !approval && <ErrorBanner error={sell.error} />}
              <Button
                className="w-full !bg-dpl-success py-4 text-lg font-semibold !text-white hover:!bg-dpl-success/90"
                busy={sell.isPending && !approval}
                disabled={!canCharge}
                onClick={() => charge()}
              >
                <LockIcon />
                Charge {cart.length > 0 ? formatMoneyDocument(total) : ''} <span className="text-xs font-normal opacity-75">F9</span>
              </Button>
              <div className="flex items-center justify-between gap-3">
                <Button
                  tone="secondary"
                  className="bg-dpl-bg px-5 py-2.5"
                  disabled={!lastReceipt}
                  title={lastReceipt ? 'The last sale on this till' : 'No sale on this till yet'}
                  onClick={() => lastReceipt && setReceipt({ ...lastReceipt, fresh: false })}
                >
                  <PrinterIcon />
                  Print receipt
                </Button>
              </div>
            </div>
          </section>
        </aside>

        <div className="min-w-0 space-y-4 xl:col-start-1 xl:row-start-2 xl:self-start">
          <RecentItems items={recent} onAdd={add} />
          <RegisterPanel session={session} locationId={locationId} />
        </div>
      </div>

      <ShortageDialog
        locationId={locationId}
        registerSessionId={session.id}
        customer={customer}
        shortage={shortage}
        /* Acts on the line the shortage was about — the box line or the loose one, not both. */
        /* Filled, not just reduced (D-272): a customer who asked for 150 and is taking the 130 the
           shop has is taking a box and thirty, at the prices that go with those. */
        onReduce={(productId, quantity) => fill(lineKey(productId, shortage?.asPack), quantity)}
        onRemove={(productId) =>
          setCart((current) => current.filter((l) => lineKey(l.productId, l.asPack, l.atPackRate) !== lineKey(productId, shortage?.asPack)))}
        /* The stock is free now, so the basket that was refused is quoted again — which is what
           takes the hold for this sale and clears the shortage off the screen. */
        onReleased={() => { setShortage(null); void quote.refetch() }}
        onClose={() => setShortage(null)}
      />

      {cart.length > 0 && !saleInView && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-dpl-border bg-dpl-surface/95 px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] backdrop-blur xl:hidden">
          <button type="button" className="flex w-full items-center justify-between gap-3 rounded-lg bg-dpl-success px-4 py-3 text-white" onClick={() => saleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
            <span className="flex items-center gap-2 text-sm font-medium"><CartIcon /> Current sale ({cart.length})</span>
            <span className="text-base font-semibold tabular-nums">{formatMoneyDocument(total)} →</span>
          </button>
        </div>
      )}

      {approval && (
        <ApprovalDialog
          locationId={locationId}
          needs={approval.needs}
          saleValue={total}
          sellerCanApprove={approval.sellerCanApprove}
          busy={sell.isPending}
          error={sell.error instanceof ApiError && sell.error.status !== 409 ? sell.error : null}
          onApprove={charge}
          onCancel={() => {
            setApproval(null)
            sell.reset()
          }}
        />
      )}

      {receipt && (
        <ReceiptDialog
          invoiceId={receipt.id}
          pointsEarned={receipt.points}
          onClose={() => {
            // A reprint leaves the sale in progress alone.
            if (receipt.fresh) reset()
            setReceipt(null)
            scanRef.current?.focus()
          }}
        />
      )}

      {chooser && (
        <Modal open title="Which product?" onClose={() => setChooser(null)}>
          <p className="mb-3 text-sm text-dpl-muted">That barcode is on more than one product. Choose the one in the customer's hand.</p>
          <ul className="divide-y divide-dpl-border">
            {chooser.map((m) => (
              <li key={m.id}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 px-1 py-2.5 text-left hover:bg-dpl-bg"
                  onClick={() => {
                    add(m)
                    setChooser(null)
                  }}
                >
                  <span>
                    <span className="block text-sm font-medium">{m.name}</span>
                    <span className="text-xs text-dpl-muted">{m.code}</span>
                  </span>
                  <span className="text-sm tabular-nums">{formatMoneyDocument(m.retailPrice)}</span>
                </button>
              </li>
            ))}
          </ul>
        </Modal>
      )}
    </div>
  )
}

/**
 * The header bar while the till is open: its name, the shop and who opened the
 * register, whether this device can reach the server, and the time in Nairobi.
 */
function TillHeader({ context, onChangeLocation }: { context: PosContext; onChangeLocation?: () => void }) {
  usePageHeader('Point of Sale', `${context.location.name} · register opened by ${context.session?.cashierName ?? ''}`)
  const online = useOnline()
  const now = useClock()
  return (
    <HeaderSlot>
      {onChangeLocation && (
        <button type="button" className="whitespace-nowrap text-sm font-medium text-dpl-primary hover:underline" onClick={onChangeLocation}>Change shop</button>
      )}
      <span role="status" className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium ${online ? 'bg-dpl-success/10 text-dpl-success' : 'bg-dpl-danger/10 text-dpl-danger'}`}>
        <span className={`h-2 w-2 rounded-full ${online ? 'bg-dpl-success' : 'bg-dpl-danger'}`} />
        <span className={online ? 'sr-only sm:not-sr-only' : ''}>{online ? 'Online' : 'Offline — sales cannot be recorded'}</span>
      </span>
      <time className="hidden whitespace-nowrap text-sm tabular-nums text-dpl-text lg:inline" dateTime={now.toISOString()}>{clockText(now)}</time>
    </HeaderSlot>
  )
}

const clock = new Intl.DateTimeFormat('en-US', { timeZone: 'Africa/Nairobi', weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })

function clockText(now: Date) {
  const parts = Object.fromEntries(clock.formatToParts(now).map((p) => [p.type, p.value]))
  return `${parts.weekday}, ${parts.day} ${parts.month} ${parts.year}  ${parts.hour}:${parts.minute}`
}

function useClock() {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 20_000)
    return () => window.clearInterval(timer)
  }, [])
  return now
}

function useOnline() {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine))
  useEffect(() => {
    const up = () => setOnline(true)
    const down = () => setOnline(false)
    window.addEventListener('online', up)
    window.addEventListener('offline', down)
    return () => {
      window.removeEventListener('online', up)
      window.removeEventListener('offline', down)
    }
  }, [])
  return online
}

/**
 * The lines, priced by the quote: item, quantity, price and total in columns, as
 * the sale panel is wide enough for from sm up. On a phone each line stacks, each
 * figure labelled from data-label, so nothing scrolls sideways. A line's own discount
 * opens in a row beneath it.
 */
function Basket({
  cart,
  quote,
  limited,
  lineNeeds,
  onUpdate,
  onFill,
  onRemove,
  onAddLooseAtBoxRate,
}: {
  cart: CartLine[]
  quote?: SaleQuote
  /** The shop cannot sell more than it has, so neither can a line. */
  limited: boolean
  lineNeeds: (productId: string) => ApprovalNeed[]
  onUpdate: (key: string, change: Partial<CartLine>) => void
  /** A quantity the cashier has finished keying, in pieces: boxes and loose are worked out from it (D-272). */
  onFill: (key: string, pieces: number) => void
  onRemove: (key: string) => void
  /** Adds loose pieces beside a box, at the box's own rate (D-235). */
  onAddLooseAtBoxRate: (line: CartLine) => void
}) {
  const [discounting, setDiscounting] = useState<string | null>(null)

  return (
    // relative: the screen-reader heading stays inside, not off the edge of the page.
    <div className="relative -mx-1">
      <table className="block w-full text-sm sm:table sm:table-fixed">
        <thead className="hidden sm:table-header-group">
          <tr className="border-b border-dpl-border text-[11px] uppercase tracking-wide text-dpl-muted">
            <th className="px-1 pb-2 text-left font-semibold">Item</th>
            <th className="w-[6.25rem] px-1 pb-2 text-center font-semibold">Qty</th>
            <th className="w-[6.75rem] px-1 pb-2 text-right font-semibold">Price</th>
            <th className="w-[6.5rem] px-1 pb-2 text-right font-semibold">Total</th>
            <th className="w-8 pb-2"><span className="sr-only">Remove</span></th>
          </tr>
        </thead>
        <tbody className="block sm:table-row-group">
          {cart.map((line) => {
            const key = lineKey(line.productId, line.asPack, line.atPackRate)
            // Matched on the unit as well as the product (D-210): a basket holding a box and a
            // loose one of the same thing has two quoted lines, and the box's price against the
            // loose line is the one mistake this toggle must not make.
            const quoted = quote?.lines.find((l) => lineKey(l.productId, l.asPack, l.atPackRate) === key)
            const needs = lineNeeds(line.productId)
            // The shelf figure is in pieces; a line sold by the box is counted in boxes, so the
            // ceiling it is compared against has to be converted or a cashier is told they can add
            // ten boxes of something there are ten pieces of (D-204).
            const perUnit = line.asPack && line.packSize ? line.packSize : 1
            // The shelf figure less what the basket's OTHER lines for this product already take,
            // then converted into this line's unit.
            const otherPieces = cart
              .filter((l) => l.productId === line.productId && lineKey(l.productId, l.asPack, l.atPackRate) !== key)
              .reduce((sum, l) => sum + l.quantity * (l.asPack && l.packSize ? l.packSize : 1), 0)
            const limit = limited && quoted ? Math.floor(Math.max(0, quoted.available - otherPieces) / perUnit) : undefined
            const overStock = quoted && line.quantity * perUnit > quoted.available
            const allOfIt = limit !== undefined && !overStock && line.quantity + 1 > limit
            const discounted = line.discountPercent > 0 || line.discountAmount > 0
            const open = discounting === key || discounted
            return (
              <Fragment key={key}>
                <tr className={`grid grid-cols-[minmax(0,1fr)_auto_auto] items-end gap-x-3 gap-y-2 px-1 py-3 sm:table-row sm:p-0 ${open ? '' : 'border-b border-dpl-border/70'}`}>
                  <td className="col-span-2 row-start-1 self-start sm:px-1 sm:py-3 sm:align-middle">
                    <div className="flex items-center gap-2.5">
                      <Picture product={line} className="h-9 w-9 shrink-0" />
                      <div className="min-w-0">
                        <span className="block font-semibold leading-5">{line.name}</span>
                        <span className="text-xs text-dpl-muted">{line.code}</span>
                        {quoted && quoted.priceSource !== 'Tier' && quoted.priceSource !== 'Default' && (
                          <span className="block text-xs text-dpl-muted">{quoted.priceSource === 'CustomerContract' ? 'contract price' : quoted.priceSource === 'Loyalty' ? 'loyalty price' : quoted.priceSource}</span>
                        )}
                        {line.atPackRate && (
                          <span className="block text-xs text-dpl-muted">at the {(line.packDescription ?? 'box').toLowerCase()} rate</span>
                        )}

                        {/* Offered on the box line, because that is where somebody is standing when
                            the customer says "and three more" (D-235). */}
                        {line.asPack && line.packSize && line.packSize > 1 && (
                          <button
                            type="button"
                            className="mt-0.5 block text-xs font-medium text-dpl-primary hover:underline"
                            onClick={() => onAddLooseAtBoxRate(line)}
                          >
                            + loose pieces at this rate
                          </button>
                        )}

                        {overStock && <span className="block text-xs text-dpl-danger">{quoted.available <= 0 ? 'Out of stock here' : `Only ${quantityText(quoted.available)} pieces in stock here`}</span>}
                        {allOfIt && <span className="block text-xs text-dpl-muted">That is all {quantityText(quoted!.available)} in stock here</span>}
                        {needs.map((n) => <span key={n.reason} className="mt-1 block text-xs text-[#8A5A00]">Needs approval: {n.reason}</span>)}
                        {/* D-204: singles or a box, on the lines where the shop sells both. The
                            quantity means boxes while "box" is chosen, and the shelf still loses
                            pieces — so switching re-quotes and the price changes under the cashier's
                            eye rather than at the end. */}
                        {line.packDescription && (
                          <span className="mt-1 flex items-center gap-1 text-xs" role="radiogroup" aria-label={`How ${line.name} is sold`}>
                            {[false, true].map((pack) => (
                              <button
                                key={String(pack)}
                                type="button"
                                role="radio"
                                aria-checked={(line.asPack === true) === pack}
                                className={`rounded border px-1.5 py-0.5 ${
                                  (line.asPack === true) === pack
                                    ? 'border-dpl-primary bg-dpl-primary text-white'
                                    : 'border-dpl-border text-dpl-muted hover:border-dpl-primary/40'
                                }`}
                                onClick={() => onUpdate(key, { asPack: pack })}
                              >
                                {pack ? line.packDescription : 'Single'}
                              </button>
                            ))}
                            {line.asPack && line.packSize && (
                              <span className="text-dpl-muted">
                                = {quantityText(line.quantity * line.packSize)} pieces
                              </span>
                            )}
                          </span>
                        )}

                        {!open && (
                          <button type="button" className="mt-0.5 flex items-center gap-1 text-xs font-medium text-dpl-primary hover:underline" onClick={() => setDiscounting(key)}>
                            <TagIcon className="h-3.5 w-3.5" /> Discount
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                  <td data-label="Qty" className={`${cellLabel} col-start-1 row-start-2 sm:px-1 sm:py-3 sm:text-center sm:align-middle`}>
                    <Stepper line={line} max={limit} onChange={(quantity) => onUpdate(key, { quantity })} onFill={(pieces) => onFill(key, pieces)} />
                  </td>
                  <td data-label="Price" className={`${cellLabel} col-start-2 row-start-2 whitespace-nowrap text-right tabular-nums sm:px-1 sm:py-3 sm:align-middle`}>
                    {quoted ? formatMoneyDocument(quoted.unitPrice) : '…'}
                  </td>
                  <td data-label="Total" className={`${cellLabel} col-start-3 row-start-2 whitespace-nowrap text-right font-bold tabular-nums sm:px-1 sm:py-3 sm:align-middle`}>
                    {quoted ? formatMoneyDocument(quoted.lineTotal) : '…'}
                  </td>
                  <td className="col-start-3 row-start-1 self-start justify-self-end sm:py-3 sm:text-right sm:align-middle">
                    <button type="button" aria-label={`Remove ${line.name}`} className="rounded p-1.5 text-dpl-muted hover:bg-dpl-danger/10 hover:text-dpl-danger" onClick={() => onRemove(key)}>
                      <TrashIcon />
                    </button>
                  </td>
                </tr>
                {open && (
                  <tr className="block border-b border-dpl-border/70 px-1 pb-3 sm:table-row sm:p-0">
                    <td colSpan={5} className="block sm:table-cell sm:px-1 sm:pb-3">
                      <div className="flex items-center gap-2 sm:pl-[2.875rem]">
                        <span className="text-xs text-dpl-muted">Discount</span>
                        <DiscountInput line={line} onChange={(change) => onUpdate(key, change)} />
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/** 12, or 40.5 for cable sold by the metre. */
function quantityText(quantity: number): string {
  return quantity.toLocaleString('en-KE', { maximumFractionDigits: 2 })
}

/** A basket cell's heading, shown above its value on a phone where the table header is not. */
const cellLabel = 'before:mb-1 before:block before:text-[11px] before:font-medium before:uppercase before:tracking-wide before:text-dpl-muted before:content-[attr(data-label)] sm:before:hidden'

/**
 * − quantity +. The box still takes a typed quantity: cable is sold by the metre.
 *
 * <b>Two callbacks, and the difference between them is the whole of D-272.</b> onChange runs on
 * every keystroke, so the price follows the cashier's eye as they type. onFill runs when they have
 * FINISHED — the field left, Enter pressed, − or + tapped — and only then is a quantity past a
 * whole box turned into boxes and loose. Splitting on the keystroke would have taken the field away
 * mid-number: "150" into a box of twelve is 1, then 15, then 150.
 */
function Stepper({ line, max, onChange, onFill }: { line: CartLine; max?: number; onChange: (quantity: number) => void; onFill: (pieces: number) => void }) {
  const atMost = max !== undefined && line.quantity + 1 > max
  const [text, setText] = useState(String(line.quantity))
  // A tap on − or +, or the same product scanned again, shows the new quantity.
  const [shown, setShown] = useState(line.quantity)
  if (shown !== line.quantity) {
    setShown(line.quantity)
    setText(String(line.quantity))
  }

  return (
    <div className="inline-flex items-center rounded-md border border-dpl-border bg-dpl-surface">
      <button type="button" aria-label={`One fewer ${line.name}`} disabled={line.quantity <= 1} className="flex h-8 w-6 items-center justify-center text-dpl-muted hover:text-dpl-text disabled:opacity-40" onClick={() => onFill(Math.max(1, line.quantity - 1))}>
        <MinusIcon className="h-4 w-4" />
      </button>
      <input
        aria-label={`Quantity of ${line.name}`}
        inputMode="decimal"
        className="h-8 w-9 border-x border-dpl-border bg-transparent text-center text-sm font-medium tabular-nums focus:outline-none focus:ring-1 focus:ring-dpl-primary"
        value={text}
        onChange={(e) => {
          setText(e.target.value)
          const n = numberOrNull(e.target.value)
          if (n !== null && n > 0) onChange(n)
          // Held to what the shop has: the box shows what the sale will take.
          if (n !== null && max !== undefined && n > max) setText(String(max > 0 ? max : line.quantity))
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            onFill(line.quantity)
          }
        }}
        onBlur={() => {
          setText(String(line.quantity))
          onFill(line.quantity)
        }}
      />
      <button
        type="button"
        aria-label={`One more ${line.name}`}
        title={atMost ? `Only ${quantityText(max)} in stock here` : undefined}
        disabled={atMost}
        className="flex h-8 w-6 items-center justify-center text-dpl-muted hover:text-dpl-text disabled:opacity-40"
        onClick={() => onFill(line.quantity + 1)}
      >
        <PlusIcon className="h-4 w-4" />
      </button>
    </div>
  )
}

/** One percentage across the whole sale. The server judges it line by line against the cashier's limit, as ever. */
/** "— 10% is yours, 20% with a manager": the ladder in the words a cashier needs at the counter. */
function discountHint(limit?: number | null, managerCeiling?: number): string {
  if (limit === null) return ' — no limit on yours'
  if (limit === undefined) return ''

  const mine = ` — ${limit}% is yours`
  return managerCeiling !== undefined && managerCeiling > limit ? `${mine}, ${managerCeiling}% with a manager` : mine
}

/**
 * The discount box, and what it tells a cashier about where they stand (D-240).
 *
 * The placeholder is the only place most cashiers ever learn their own authority, so it names the
 * shop's line and what lies above it. It does not *stop* them typing more: a cashier who needs 14%
 * should be able to put it in and be told who can approve it, rather than be silently clipped and
 * left guessing — which is how this once lost a line off a sale altogether.
 */
function SaleDiscount({ cart, limit, managerCeiling, onApply }: { cart: CartLine[]; limit?: number | null; managerCeiling?: number; onApply: (percent: number) => void }) {
  const current = cart.every((l) => l.discountAmount === 0 && l.discountPercent === cart[0].discountPercent) ? cart[0].discountPercent : null
  const [text, setText] = useState('')
  const value = numberOrNull(text)
  const valid = value !== null && value >= 0 && value <= 100

  return (
    <form
      className="flex gap-2"
      onSubmit={(e) => {
        e.preventDefault()
        if (valid) {
          onApply(value)
          setText('')
        }
      }}
    >
      <label className="flex min-w-0 flex-1 items-center gap-2 rounded-md border border-dpl-border bg-dpl-surface px-3 focus-within:border-dpl-primary">
        <TagIcon className="h-4 w-4 shrink-0 text-dpl-muted" />
        <span className="sr-only">Discount on the whole sale (%)</span>
        <input
          inputMode="decimal"
          className="h-10 min-w-0 flex-1 bg-transparent text-sm focus:outline-none"
          placeholder={current ? `${current}% on every line` : `Discount (%)${discountHint(limit, managerCeiling)}`}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </label>
      <Button type="submit" tone="secondary" disabled={!valid}>Apply</Button>
    </form>
  )
}

function TenderRow({
  index,
  tender,
  locationId,
  customer,
  loyaltyPoints,
  total,
  others,
  onMode,
  onChange,
  onExact,
  onRemove,
  onPayer,
}: {
  index: number
  tender: Tender
  locationId: string
  customer: PosCustomer
  loyaltyPoints?: number
  total: number
  others: number
  onMode: (mode: PaymentMode) => void
  onChange: (change: Partial<Tender>) => void
  onExact: () => void
  onRemove?: () => void
  /** Whose number paid, once Safaricom confirms it (D-202). */
  onPayer: (payer: { id: string; name: string; wasCreated: boolean }) => void
}) {
  const label = tenderLabels[tender.mode]
  return (
    <div className={index > 0 ? 'rounded-md border border-dpl-border p-3' : ''}>
      <div className="flex flex-wrap items-center gap-2">
        {index > 0 && (
          <Select aria-label={`Tender ${index + 1}`} className="w-36" value={tender.mode} disabled={Boolean(tender.mpesaPaymentId)} onChange={(e) => onMode(e.target.value as PaymentMode)}>
            {TENDERS.filter((m) => m !== 'CreditSale').map((m) => (
              <option key={m} value={m} disabled={m === 'LoyaltyPoints' && !loyaltyPoints}>{tenderLabels[m]}</option>
            ))}
          </Select>
        )}
        {tender.mode !== 'CreditSale' && (
          <label className={`flex min-w-0 items-center gap-3 ${index === 0 ? 'flex-1' : ''}`}>
            {index === 0 && <span className="shrink-0 text-sm font-semibold">{tender.mode === 'Cash' ? 'Amount received' : `${label} amount`}</span>}
            <TextInput
              aria-label={index === 0 && tender.mode === 'Cash' ? 'Cash amount received' : `${label} amount`}
              inputMode="decimal"
              className={index === 0 ? 'min-w-0 flex-1 py-2.5 text-base tabular-nums' : 'w-32'}
              value={tender.amount ? String(tender.amount) : ''}
              disabled={Boolean(tender.mpesaPaymentId)}
              placeholder="0.00"
              onChange={(e) => onChange({ amount: numberOrNull(e.target.value) ?? 0 })}
            />
          </label>
        )}
        {tender.mode !== 'CreditSale' && !tender.mpesaPaymentId && (
          <Button tone="secondary" className={index === 0 ? 'py-2.5' : ''} onClick={onExact}>Exact</Button>
        )}
        {onRemove && <Button tone="ghost" aria-label="Remove tender" onClick={onRemove}>×</Button>}
      </div>
      {needsReference.includes(tender.mode) && (
        <TextInput
          aria-label={`${label} reference`}
          className="mt-2 uppercase"
          placeholder={tender.mode === 'Card' ? 'Card slip or approval code' : 'Reference'}
          value={tender.reference ?? ''}
          onChange={(e) => onChange({ reference: e.target.value })}
        />
      )}
      {tender.mode === 'Mpesa' && (
        <div className="mt-2">
          <MpesaTender
            locationId={locationId}
            amount={tender.amount}
            phone={customer.isWalkIn ? undefined : customer.phone}
            paidCode={tender.mpesaCode}
            onPaid={(p) => {
              onChange({ amount: p.amount, mpesaPaymentId: p.id, mpesaCode: p.code })
              if (p.payer) onPayer(p.payer)
            }}
            onClear={() => onChange({ mpesaPaymentId: undefined, mpesaCode: undefined })}
          />
        </div>
      )}
      {tender.mode === 'ExchangeCredit' && (
        <div className="mt-2">
          <ExchangeCreditTender
            customerId={customer.id}
            customerIsWalkIn={customer.isWalkIn}
            claimedCode={tender.creditNoteCode}
            onFound={(c) => {
              const due = Math.max(0, Math.round((total - others) * 100) / 100)
              onChange({ amount: Math.min(c.remaining, due || c.remaining), creditNoteId: c.id, creditNoteCode: c.code, creditRemaining: c.remaining })
            }}
            onClear={() => onChange({ creditNoteId: undefined, creditNoteCode: undefined, creditRemaining: undefined })}
          />
        </div>
      )}
    </div>
  )
}

function Row({ label, value }: { label: string; value?: number }) {
  return (
    <div className="flex justify-between">
      <dt className="text-dpl-text/80">{label}</dt>
      <dd className="tabular-nums text-dpl-text">{value === undefined ? '—' : formatMoneyDocument(value)}</dd>
    </div>
  )
}

/** Percentage or shillings, per line. The server judges both together against the cashier's limit. */
function DiscountInput({ line, onChange }: { line: CartLine; onChange: (change: Partial<CartLine>) => void }) {
  const [unit, setUnit] = useState<'%' | 'KSh'>(line.discountAmount > 0 ? 'KSh' : '%')
  const value = unit === '%' ? line.discountPercent : line.discountAmount

  return (
    <div className="flex items-center gap-1">
      <TextInput
        aria-label={`Discount on ${line.name}`}
        inputMode="decimal"
        className="w-20 py-1.5"
        value={value ? String(value) : ''}
        placeholder="0"
        autoFocus={value === 0}
        onChange={(e) => {
          const n = Math.max(0, numberOrNull(e.target.value) ?? 0)
          onChange(unit === '%' ? { discountPercent: Math.min(100, n), discountAmount: 0 } : { discountAmount: n, discountPercent: 0 })
        }}
      />
      <Select
        aria-label={`Discount type on ${line.name}`}
        className="w-20 py-1.5"
        value={unit}
        onChange={(e) => {
          setUnit(e.target.value as '%' | 'KSh')
          onChange({ discountPercent: 0, discountAmount: 0 })
        }}
      >
        <option value="%">%</option>
        <option value="KSh">KSh</option>
      </Select>
    </div>
  )
}

/**
 * The scanner box, which also searches the shelf. A barcode scanner types the code and
 * presses Enter; so can a person. Typing without Enter narrows the products below.
 */
function Scanner({
  inputRef,
  locationId,
  term,
  onTerm,
  onAdd,
  onChoose,
}: {
  inputRef: React.RefObject<HTMLInputElement | null>
  /** The shop at the counter, so a scan is priced the way this shop prices it (D-276). */
  locationId: string
  term: string
  onTerm: (term: string) => void
  onAdd: (p: PickedProduct) => void
  onChoose: (matches: LookupMatch[]) => void
}) {
  const [typed, setTyped] = useState(term)
  const [message, setMessage] = useState<string | null>(null)

  // The shelf follows the box a quarter of a second behind, not on every keystroke.
  useEffect(() => {
    const timer = window.setTimeout(() => onTerm(typed), 250)
    return () => window.clearTimeout(timer)
  }, [typed, onTerm])

  function clear() {
    setTyped('')
    onTerm('')
  }

  async function submit() {
    const code = typed.trim()
    if (!code) return
    setMessage(null)

    try {
      // With the shop, so a scan shows what THIS counter charges (D-276).
      const matches = await api<LookupMatch[]>(
        `/api/products/lookup?code=${encodeURIComponent(code)}${locationId ? `&locationId=${locationId}` : ''}`,
      )
      const active = matches.filter((m) => m.isActive)

      if (active.length === 1) {
        onAdd(active[0])
        clear()
      } else if (active.length > 1) {
        onChoose(active)
        clear()
      } else {
        // Not a barcode or a code: a name. One match adds it; more leave the shelf to choose from.
        const found = await api<{ items: PickedProduct[] }>(
          `/api/products?status=active&page=1&pageSize=2&search=${encodeURIComponent(code)}${locationId ? `&locationId=${locationId}` : ''}`,
        )
        if (found.items.length === 1) {
          onAdd(found.items[0])
          clear()
        } else if (found.items.length === 0) {
          setMessage(`Nothing matches “${code}”.`)
        }
      }
    } catch (error) {
      setMessage(describeError(error))
    }
  }

  return (
    <div>
      <div className="flex items-center gap-3 rounded-lg border border-dpl-border bg-dpl-surface px-4 shadow-sm focus-within:border-dpl-primary focus-within:ring-1 focus-within:ring-dpl-primary">
        <SearchIcon className="h-5 w-5 shrink-0 text-dpl-muted" />
        <label htmlFor="pos-scan" className="sr-only">Scan or search</label>
        <input
          id="pos-scan"
          ref={inputRef}
          autoFocus
          autoComplete="off"
          placeholder="Scan a barcode, type a product name or code…"
          className="h-12 min-w-0 flex-1 bg-transparent text-base focus:outline-none"
          value={typed}
          onChange={(e) => {
            setTyped(e.target.value)
            setMessage(null)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              void submit()
            }
            if (e.key === 'Escape') clear()
          }}
        />
        <BarcodeIcon className="h-6 w-6 shrink-0 text-dpl-muted" />
      </div>
      {message && <p className="mt-1 text-sm text-dpl-danger">{message}</p>}
    </div>
  )
}

/** The last product's suggested companions (milestone 10's upsells), one tap to add. */
function Suggestions({ productId, inCart, onAdd }: { productId: string; inCart: string[]; onAdd: (p: PickedProduct) => void }) {
  const product = useProduct(productId)
  const upsells = (product.data?.upsells ?? []).filter((u) => u.isActive && !inCart.includes(u.id))
  const talkingPoint = product.data?.talkingPoints?.split('\n').find((l) => l.trim())

  if (upsells.length === 0 && !talkingPoint) return null

  return (
    <div className="rounded-md bg-dpl-bg px-3 py-2.5 text-sm">
      {talkingPoint && (
        <p className="text-dpl-text">
          <span className="font-medium">Say:</span> {talkingPoint}
        </p>
      )}
      {upsells.length > 0 && (
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          <span className="text-dpl-muted">Suggest with {product.data?.name}:</span>
          {upsells.map((u) => (
            <Button key={u.id} tone="secondary" onClick={() => onAdd(u)}>
              + {u.name}
            </Button>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * The customer — by name, account number or phone (D-243).
 *
 * Found → their record and price tier. A phone nobody has → created on the spot. Skipping leaves
 * the walk-in record, which the capture rate counts.
 *
 * It used to take a phone and nothing else, and the phone had to parse. A trade customer who knew
 * their account number but not which number the account was opened with could not be found at all,
 * and their sale went through as a walk-in at retail prices. So the box now takes whatever the
 * cashier has been told and shows the matches to choose from, with the account number and phone
 * beside each name — two customers called Kamau is the normal case, and putting a sale on the wrong
 * account is worse than not finding either.
 *
 * Creating is still phone-only, deliberately: a new customer with no number is a record nobody can
 * ever match again, and the receipt, the delivery and the warranty reminder all need it.
 */
function CustomerPanel({
  locationId,
  customer,
  walkIn,
  onChange,
}: {
  locationId: string
  customer: PosCustomer
  walkIn: PosCustomer
  onChange: (customer: PosCustomer) => void
}) {
  const capture = useCaptureCustomer()
  const [term, setTerm] = useState('')
  const [name, setName] = useState('')

  // Debounced so a cashier typing a name does not send a request per keystroke on a till that may
  // be on a phone tether.
  const debounced = useDebounced(term, 250)
  const matches = useCustomerSearch(debounced)

  // Only a phone can be created. Anything else is a search and nothing more.
  const isPhone = /^[+0-9][0-9\s-]{6,}$/.test(term.trim())

  if (!customer.isWalkIn) {
    return (
      <div className="rounded-md border border-dpl-border px-3 py-2.5 text-sm">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-start gap-2">
            <UserIcon className="mt-0.5 h-5 w-5 shrink-0 text-dpl-primary" />
            <div className="min-w-0">
              <p className="font-medium">{customer.name}</p>
              <p className="text-xs text-dpl-muted">{customer.code} · {customer.phone} · {customer.priceTier} prices</p>
            </div>
          </div>
          <Button tone="ghost" className="px-2" onClick={() => onChange(walkIn)}>Change</Button>
        </div>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {customer.vatExemptionCertificate && <Badge tone="info">VAT exempt · {customer.vatExemptionCertificate}</Badge>}
          {customer.isBlocked && <Badge tone="danger">Account blocked</Badge>}
          {customer.creditLimit > 0 && <Badge tone={customer.availableCredit > 0 ? 'success' : 'warning'}>Credit {formatMoneyDocument(customer.availableCredit)} available</Badge>}
        </div>
      </div>
    )
  }

  const found = matches.data ?? []

  function pick(picked: PosCustomer) {
    onChange(picked)
    setTerm('')
    setName('')
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()

        // Enter on a single match takes it — a cashier who typed an account number and got one
        // answer should not have to reach for the mouse.
        if (found.length === 1) {
          pick(found[0])
          return
        }

        if (!isPhone) return

        capture.mutate(
          { phone: term.trim(), name: name || undefined, locationId },
          { onSuccess: pick },
        )
      }}
    >
      <div className="flex items-center gap-2">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-dpl-bg text-dpl-muted" title="Walk-in customer">
          <UserIcon />
        </span>
        <TextInput
          aria-label="Customer name, number or phone"
          placeholder="Name, account number or phone"
          className="min-w-0 flex-1"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
        />
        <Button type="submit" tone="secondary" busy={capture.isPending || matches.isFetching} disabled={!isPhone && found.length !== 1}>
          {isPhone && found.length === 0 ? 'Add' : 'Find'}
        </Button>
      </div>

      {found.length > 0 && (
        <ul className="ml-12 mt-2 divide-y divide-dpl-border overflow-hidden rounded-md border border-dpl-border">
          {found.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-dpl-bg"
                onClick={() => pick(c)}
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium">{c.name}</span>
                  {/* The two facts that tell two customers of the same name apart. */}
                  <span className="block truncate text-xs text-dpl-muted">
                    {c.code}
                    {c.phone ? ` · ${c.phone}` : ''} · {c.priceTier} prices
                  </span>
                </span>
                <span className="flex shrink-0 gap-1.5">
                  {c.isBlocked && <Badge tone="danger">Blocked</Badge>}
                  {c.creditLimit > 0 && <Badge tone={c.availableCredit > 0 ? 'success' : 'warning'}>Credit</Badge>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Nothing found, and what was typed could be a phone: offer to create them. */}
      {isPhone && found.length === 0 && debounced.trim().length >= 2 && !matches.isFetching && (
        <TextInput
          aria-label="Customer name (if new)"
          placeholder="Nobody has that number — their name, to add them"
          className="ml-12 mt-2 w-[calc(100%-3rem)]"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      )}

      {!isPhone && debounced.trim().length >= 2 && found.length === 0 && !matches.isFetching && (
        <p className="ml-12 mt-2 text-xs text-dpl-muted">
          Nobody by that name or number. Try their phone, which can also add them.
        </p>
      )}

      <div className="mt-2 empty:hidden"><ErrorBanner error={capture.error} /></div>
      <div className="mt-2 empty:hidden"><ErrorBanner error={matches.error} /></div>
    </form>
  )
}
